import * as BABYLON from '@babylonjs/core'
import { FurnitureKind } from './roomPresets'

export interface BuiltFurniture {
  root: BABYLON.TransformNode
  meshes: BABYLON.Mesh[]
  /** Rough footprint on the floor, meters — used for spawn spacing and wall clamping, not real collision. */
  footprint: { w: number; d: number }
}

/**
 * Every piece is built from primitives (box/cylinder/cone/sphere) — no
 * external model loading, so this never depends on a real furniture asset
 * pipeline existing yet. Good enough to prove out placement/move/rotate;
 * swapping in real GLB catalog items later is a materials/mesh-source
 * change, not a placement-logic one.
 */
export function buildFurnitureMesh(scene: BABYLON.Scene, kind: FurnitureKind, seq: number): BuiltFurniture {
  const root = new BABYLON.TransformNode(`furniture-${kind}-${seq}`, scene)
  const meshes: BABYLON.Mesh[] = []
  const add = (m: BABYLON.Mesh, y: number, x = 0, z = 0) => {
    m.position.set(x, y, z)
    m.parent = root
    meshes.push(m)
    return m
  }
  const mat = (name: string, hex: string, opts: { metallic?: number; roughness?: number; emissive?: string } = {}) => {
    const m = new BABYLON.PBRMaterial(`${name}-${seq}`, scene)
    m.albedoColor = BABYLON.Color3.FromHexString(hex)
    m.metallic = opts.metallic ?? 0
    m.roughness = opts.roughness ?? 0.7
    if (opts.emissive) m.emissiveColor = BABYLON.Color3.FromHexString(opts.emissive)
    return m
  }

  let footprint = { w: 0.6, d: 0.6 }

  switch (kind) {
    case 'bed': {
      const linen = mat('bed-linen', '#8a6a78', { roughness: 0.85 })
      const wood = mat('bed-wood', '#4a3628', { roughness: 0.6 })
      add(BABYLON.MeshBuilder.CreateBox('bedMattress', { width: 1.6, height: 0.32, depth: 2.1 }, scene), 0.16).material = linen
      add(BABYLON.MeshBuilder.CreateBox('bedHeadboard', { width: 1.7, height: 0.7, depth: 0.1 }, scene), 0.35, 0, -1.05).material = wood
      ;[-0.5, 0.5].forEach((x) => {
        add(BABYLON.MeshBuilder.CreateBox('bedPillow', { width: 0.5, height: 0.14, depth: 0.35 }, scene), 0.39, x, -0.75).material = mat(
          'bed-pillow',
          '#efe6dc',
          { roughness: 0.9 },
        )
      })
      footprint = { w: 1.7, d: 2.1 }
      break
    }
    case 'sofa': {
      const fabric = mat('sofa-fabric', '#5c6b63', { roughness: 0.85 })
      add(BABYLON.MeshBuilder.CreateBox('sofaSeat', { width: 1.8, height: 0.4, depth: 0.75 }, scene), 0.22).material = fabric
      add(BABYLON.MeshBuilder.CreateBox('sofaBack', { width: 1.8, height: 0.55, depth: 0.16 }, scene), 0.55, 0, -0.3).material = fabric
      ;[-0.9, 0.9].forEach((x) => {
        add(BABYLON.MeshBuilder.CreateBox('sofaArm', { width: 0.16, height: 0.42, depth: 0.75 }, scene), 0.35, x, 0).material = fabric
      })
      footprint = { w: 2, d: 0.85 }
      break
    }
    case 'desk': {
      const wood = mat('desk-wood', '#6b4a34', { roughness: 0.5 })
      const metal = mat('desk-leg', '#232228', { metallic: 0.7, roughness: 0.4 })
      add(BABYLON.MeshBuilder.CreateBox('deskTop', { width: 1.3, height: 0.05, depth: 0.6 }, scene), 0.72).material = wood
      ;[[-0.58, -0.25], [0.58, -0.25], [-0.58, 0.25], [0.58, 0.25]].forEach(([x, z]) => {
        add(BABYLON.MeshBuilder.CreateCylinder('deskLeg', { diameter: 0.04, height: 0.7 }, scene), 0.35, x, z).material = metal
      })
      footprint = { w: 1.4, d: 0.7 }
      break
    }
    case 'coffeeTable': {
      const wood = mat('coffee-wood', '#5a4230', { roughness: 0.5 })
      add(BABYLON.MeshBuilder.CreateBox('coffeeTop', { width: 0.95, height: 0.05, depth: 0.55 }, scene), 0.32).material = wood
      ;[[-0.4, -0.2], [0.4, -0.2], [-0.4, 0.2], [0.4, 0.2]].forEach(([x, z]) => {
        add(BABYLON.MeshBuilder.CreateCylinder('coffeeLeg', { diameter: 0.04, height: 0.32 }, scene), 0.16, x, z).material = wood
      })
      footprint = { w: 1, d: 0.6 }
      break
    }
    case 'shelf': {
      const wood = mat('shelf-wood', '#4a3324', { roughness: 0.6 })
      add(BABYLON.MeshBuilder.CreateBox('shelfBody', { width: 0.8, height: 1.8, depth: 0.32 }, scene), 0.9).material = wood
      ;[0.35, 0.9, 1.45].forEach((y) => {
        add(BABYLON.MeshBuilder.CreateBox('shelfBoard', { width: 0.86, height: 0.03, depth: 0.34 }, scene), y).material = mat(
          'shelf-board',
          '#3a2618',
          { roughness: 0.55 },
        )
      })
      footprint = { w: 0.9, d: 0.4 }
      break
    }
    case 'lamp': {
      const metal = mat('lamp-metal', '#2b2b30', { metallic: 0.6, roughness: 0.4 })
      const shade = mat('lamp-shade', '#ffdba8', { emissive: '#7a5424' })
      add(BABYLON.MeshBuilder.CreateCylinder('lampBase', { diameter: 0.22, height: 0.03 }, scene), 0.015).material = metal
      add(BABYLON.MeshBuilder.CreateCylinder('lampPole', { diameter: 0.03, height: 1.0 }, scene), 0.5).material = metal
      add(BABYLON.MeshBuilder.CreateCylinder('lampShade', { diameterTop: 0.1, diameterBottom: 0.22, height: 0.22 }, scene), 1.1).material = shade
      const glow = new BABYLON.PointLight(`lampGlow-${seq}`, new BABYLON.Vector3(0, 1.1, 0), scene)
      glow.diffuse = BABYLON.Color3.FromHexString('#ffcf7d')
      glow.intensity = 0.35
      glow.range = 6
      glow.parent = root
      footprint = { w: 0.3, d: 0.3 }
      break
    }
    case 'plant': {
      const pot = mat('plant-pot', '#a15c3c', { roughness: 0.75 })
      const leaf = mat('plant-leaf', '#3f6b45', { roughness: 0.8 })
      add(BABYLON.MeshBuilder.CreateCylinder('plantPot', { diameterTop: 0.32, diameterBottom: 0.24, height: 0.32 }, scene), 0.16).material = pot
      ;[
        [0, 0.55, 0, 0.14],
        [0.12, 0.45, 0.08, 0.11],
        [-0.13, 0.42, -0.06, 0.1],
        [0.02, 0.62, -0.1, 0.09],
      ].forEach(([x, y, z, r]) => {
        add(BABYLON.MeshBuilder.CreateSphere('plantLeaf', { diameter: r * 2 }, scene), y, x, z).material = leaf
      })
      footprint = { w: 0.4, d: 0.4 }
      break
    }
    case 'rug': {
      add(BABYLON.MeshBuilder.CreateBox('rug', { width: 1.9, height: 0.02, depth: 1.3 }, scene), 0.011).material = mat('rug-mat', '#a4664a', {
        roughness: 0.95,
      })
      footprint = { w: 1.9, d: 1.3 }
      break
    }
    case 'easel': {
      const wood = mat('easel-wood', '#5a4230', { roughness: 0.55 })
      const canvasMat = mat('easel-canvas', '#f2ece1', { roughness: 0.9 })
      ;[-0.24, 0.24].forEach((x) => {
        const leg = BABYLON.MeshBuilder.CreateCylinder('easelLeg', { diameter: 0.035, height: 1.3 }, scene)
        leg.rotation.z = x > 0 ? -0.12 : 0.12
        add(leg, 0.6, x, -0.05).material = wood
      })
      const backLeg = BABYLON.MeshBuilder.CreateCylinder('easelBackLeg', { diameter: 0.035, height: 1.2 }, scene)
      backLeg.rotation.x = 0.32
      add(backLeg, 0.55, 0, 0.35).material = wood
      add(BABYLON.MeshBuilder.CreateBox('easelCanvas', { width: 0.55, height: 0.7, depth: 0.03 }, scene), 0.95, 0, -0.15).material = canvasMat
      footprint = { w: 0.6, d: 0.6 }
      break
    }
  }

  return { root, meshes, footprint }
}
